/**
 * ProgressBar — drip progress bar (05-UI-SPEC.md Surface 6, Plan 05-09).
 *
 * Props:
 *   value (0–100)    — current percentage fill
 *   colorVariant     — 'success' | 'warning' | 'muted'
 *
 * Container:  w-full h-2 bg-bg-input rounded-full overflow-hidden
 * Fill:       h-full transition-[width] duration-500
 *   success → bg-success
 *   warning → bg-warning
 *   muted   → bg-text-subtle
 *
 * Accessibility:
 *   role="progressbar" aria-valuenow={value} aria-valuemin={0} aria-valuemax={100}
 *   (05-UI-SPEC.md §Accessibility)
 */

import * as React from 'react';

export type ProgressBarVariant = 'success' | 'warning' | 'muted';

interface ProgressBarProps {
  /** Current progress value 0–100 */
  value: number;
  /** Color variant (default: 'muted') */
  colorVariant?: ProgressBarVariant;
  /** Additional class on the container */
  className?: string;
}

const fillClasses: Record<ProgressBarVariant, string> = {
  success: 'bg-success',
  warning: 'bg-warning',
  muted: 'bg-text-subtle',
};

export function ProgressBar({
  value,
  colorVariant = 'muted',
  className = '',
}: ProgressBarProps) {
  // Clamp value to [0, 100]
  const clamped = Math.min(100, Math.max(0, value));

  return (
    <div
      role="progressbar"
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={100}
      className={['w-full h-2 bg-bg-input rounded-full overflow-hidden', className]
        .filter(Boolean)
        .join(' ')}
    >
      <div
        className={[
          'h-full rounded-full transition-[width] duration-500',
          fillClasses[colorVariant],
        ].join(' ')}
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
}
